import React, { useCallback, useEffect, useState } from 'react';
import { ThemeCtx, ThemeMode, getTheme, themeCssVars, FONT_UI, RADIUS, TYPE, useTheme } from '../theme';
import { BrandLogo } from '../components/BrandLogo';
import { Button, Card, Field, Input } from '../components/ui';
import { api, type SigningView } from '../api';
import { SignatureCanvas } from '../admission/SignatureCanvas';

// P3/KO1/KO2 · /avtal/<token>: läs avtalet och signera med skrivet namn + ritad signatur (D2).
// Länken kommer med mejl (och från Mina köp / markens Ekonomi). Ingen inloggning – länken är personlig.
// BankID visas som en kommande metod tills Qid/Idura kopplas in.

export const SIGNING_PATH = '/avtal/';
export const signingTokenFromPath = (pathname: string) =>
  pathname.startsWith(SIGNING_PATH) ? decodeURIComponent(pathname.slice(SIGNING_PATH.length).split('/')[0] || '') : '';

const when = (ms: number) => new Date(ms).toLocaleString('sv-SE', { dateStyle: 'long', timeStyle: 'short' });

export function SigningPage({ token }: { token: string }) {
  const T = useTheme();
  const [view, setView] = useState<SigningView | null>(null);
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [signature, setSignature] = useState('');
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.signingView(token).then(v => { setView(v); setName(v.signerName ?? ''); }).catch(e => setLoadError(e.message));
  }, [token]);

  async function sign(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (name.trim().split(/\s+/).length < 2) { setError('Skriv ditt för- och efternamn'); return; }
    if (!signature) { setError('Rita din signatur i rutan'); return; }
    if (!accept) { setError('Bekräfta att du har läst avtalet'); return; }
    setBusy(true);
    try { setView(await api.signAgreement(token, { method: 'drawn', name: name.trim(), signature, accept })); }
    catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  }

  const wrap = (children: React.ReactNode) => (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px 64px', color: T.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <BrandLogo size={28} /><span style={{ ...TYPE.label, color: T.textMuted }}>Avtal</span>
      </div>
      {children}
    </div>
  );

  if (loadError) return wrap(<Card><div style={{ ...TYPE.title, marginBottom: 6 }}>Länken gäller inte</div><div style={{ color: T.textSub }}>{loadError}</div></Card>);
  if (!view) return wrap(<div style={{ color: T.textSub }}>Hämtar avtalet …</div>);

  const isPdf = view.file?.mime === 'application/pdf';
  const signed = view.status === 'signed';
  return wrap(<>
    <h1 style={{ ...TYPE.page, fontSize: 28, margin: '0 0 6px' }}>{view.title}</h1>
    <div style={{ color: T.textSub, marginBottom: 20 }}>
      {view.purpose === 'platform' ? 'Avtal mellan Qvenari och markägaren' : `Från ${view.markName ?? 'jaktmarken'}`} · till {view.signerName || view.signerEmail}
    </div>

    {signed && (
      <Card style={{ marginBottom: 20, borderColor: T.ok }} data-testid="signing-done">
        <div style={{ ...TYPE.title, color: T.ok, marginBottom: 4 }}>Avtalet är signerat</div>
        <div style={{ color: T.textSub, marginBottom: 12 }}>Signerat av {view.signedName} {view.signedAt ? when(view.signedAt) : ''}.{view.purpose === 'admission' ? ' Du ligger nu i gruppen på marken och hittar den under Min jakt.' : ''}</div>
        <a href={api.signingFileUrl(token, 'signed.pdf')} target="_blank" rel="noopener" style={{ color: T.accentText, fontWeight: 700, display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>Öppna det signerade avtalet (PDF)</a>
      </Card>
    )}

    {view.bodyText && (
      <Card style={{ marginBottom: 16 }}>
        <div data-testid="signing-text" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 15 }}>{view.bodyText}</div>
      </Card>
    )}
    {view.file && (
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 10 }}>
          <div style={{ fontWeight: 700 }}>{view.file.name}</div>
          <a href={api.signingFileUrl(token, 'document')} target="_blank" rel="noopener" style={{ color: T.accentText, fontWeight: 600, display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>
            {isPdf ? 'Öppna PDF:en' : 'Ladda ned originalet'}
          </a>
        </div>
        {isPdf
          ? <iframe title={view.file.name} src={api.signingFileUrl(token, 'document')} style={{ width: '100%', height: '70vh', border: `1px solid ${T.border}`, borderRadius: RADIUS.sm, background: '#fff' }} />
          : view.file.text && <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 15, maxHeight: '70vh', overflow: 'auto' }}>{view.file.text}</div>}
      </Card>
    )}

    {view.status === 'sent' && (
      <Card>
        <form onSubmit={sign} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ ...TYPE.title }}>Signera</div>
          <Field label="Ditt för- och efternamn">
            <Input value={name} onChange={e => setName(e.target.value)} autoComplete="name" data-testid="signing-name" />
          </Field>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Signatur</div>
            <SignatureCanvas onChange={setSignature} />
          </div>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, cursor: 'pointer' }}>
            <input type="checkbox" checked={accept} onChange={e => setAccept(e.target.checked)} data-testid="signing-accept" style={{ width: 20, height: 20, marginTop: 1, flexShrink: 0 }} />
            <span>Jag har läst avtalet och godkänner det. Jag förstår att min signatur, tidpunkten, IP-adressen och enheten sparas som bevis.</span>
          </label>
          {error && <div role="alert" style={{ color: T.danger, fontWeight: 600 }}>{error}</div>}
          <Button type="submit" size="lg" block loading={busy} data-testid="signing-submit">Signera avtalet</Button>
          {view.methods.filter(m => m.id !== 'drawn').map(m => (
            <div key={m.id} style={{ fontSize: 13, color: T.textMuted, textAlign: 'center' }}>{m.label}{m.available ? '' : ' – kommer senare'}</div>
          ))}
        </form>
      </Card>
    )}
    {view.status === 'cancelled' && <Card><div style={{ color: T.textSub }}>Det här utskicket gäller inte längre – ett nyare avtal kan ha skickats till dig.</div></Card>}
  </>);
}

/** Publik sida med eget tema (sparat läge eller systemets), som inbjudningssidan. */
export function PublicSigningPage({ token }: { token: string }) {
  const [mode, setMode] = useState<ThemeMode>(() => (localStorage.getItem('qvenari_theme') as ThemeMode) || (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'));
  const toggle = useCallback(() => setMode(m => { const n = m === 'dark' ? 'light' : 'dark'; localStorage.setItem('qvenari_theme', n); return n; }), []);
  const T = getTheme(mode);
  return (
    <ThemeCtx.Provider value={{ mode, toggle }}>
      <style>{`${themeCssVars(T)} * { box-sizing: border-box; } body { margin: 0; background: ${T.bg}; font-family: ${FONT_UI}; } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ fontFamily: FONT_UI, minHeight: '100vh', background: T.bg }}>
        <SigningPage token={token} />
      </div>
    </ThemeCtx.Provider>
  );
}
